import type { CurrencyIdentifier } from "@opensea/app-kit"
import { useCurrencies } from "@opensea/app-kit"
import type { FragmentOf } from "@opensea/graphql"
import { readFragment } from "@opensea/graphql"
import {
  classNames,
  Flex,
  Media,
  NumberDisplay,
  Separator,
  Skeleton,
} from "@opensea/ui-kit"
import { Controller, useFormContext, useWatch } from "react-hook-form"
import { useTranslations } from "use-intl"
import type { z } from "zod"
import { calculateOrderTotals } from "../../lib/fees"
import type { orderSchema } from "../../lib/zod/schema"
import {
  getFloorPriceDifference,
  OrdersSummary,
  OrdersSummaryItem,
  OrdersSummaryItemContent,
  OrdersSummaryItemDescription,
  OrdersSummaryItemSide,
  OrdersSummaryItemTitle,
  OrdersSummaryTokenValue,
  OrdersSummaryTokenValueSkeleton,
} from "../OrdersSummary"
import { CreateOrdersSummaryFragment } from "./CreateOrdersSummary.fragment"
import { CreatorEarningsToggle } from "./CreatorEarningsToggle"
import { CreatorEarningsTooltip } from "./CreatorEarningsTooltip"

type CreateOrdersSummaryProps = {
  className?: string
  symbol: string
  adjustForFees: boolean
  orders: z.infer<typeof orderSchema>[]
  currencyIdentifier: CurrencyIdentifier
  isReview?: boolean
  collection?: FragmentOf<typeof CreateOrdersSummaryFragment>
  orderSide: "listing" | "offer"
  creatorFeeEnforced: boolean
}

export function CreateOrdersSummary({
  symbol,
  adjustForFees,
  orders,
  currencyIdentifier,
  isReview,
  collection: collectionKey,
  orderSide,
  creatorFeeEnforced,
}: CreateOrdersSummaryProps) {
  const t = useTranslations("wallet.CreateOrdersSummary")
  const collection = readFragment(CreateOrdersSummaryFragment, collectionKey)

  const { getUsdPrice } = useCurrencies()

  const { withoutFees, afterFees, adjustedForFees, creatorFee, openseaFee } =
    calculateOrderTotals(orders)

  const totalValue = adjustForFees ? adjustedForFees : withoutFees
  const totalValueUsd = getUsdPrice(currencyIdentifier, totalValue)
  const totalProceeds = adjustForFees ? withoutFees : afterFees

  const creatorFeePercentage = creatorFee / withoutFees
  const openseaFeePercentage = openseaFee / withoutFees
  const displayCreatorFee =
    orders.some(order => order.creatorFeeBasisPoints > 0) &&
    (creatorFeeEnforced || orderSide === "listing")

  const floorPriceUsd = collection?.floorPrice?.pricePerItem.usd
  // Only calculate floor price difference if there is only one order
  const floorPriceDifference =
    orders.length === 1
      ? getFloorPriceDifference(
          floorPriceUsd,
          totalValueUsd,
          orders[0].quantity,
        )
      : undefined

  const { control } = useFormContext()
  const applyCreatorFee = useWatch({ control, name: "applyCreatorFee" })

  const creatorFeeToggle =
    orderSide === "listing" ? (
      <Controller
        control={control}
        disabled={isReview}
        name="applyCreatorFee"
        render={({ field }) => (
          <CreatorEarningsToggle
            checked={field.value}
            creatorFeeEnforced={creatorFeeEnforced}
            disabled={field.disabled}
            onCheckedChange={value => {
              field.onChange(value)
            }}
          />
        )}
      />
    ) : null

  return (
    <OrdersSummary>
      <OrdersSummaryItem>
        <OrdersSummaryItemContent>
          <OrdersSummaryItemTitle>
            {orderSide === "offer"
              ? t("totalOfferValue")
              : t("totalListingPrice")}
          </OrdersSummaryItemTitle>
        </OrdersSummaryItemContent>
        <OrdersSummaryItemSide>
          <OrdersSummaryTokenValue
            isFee={false}
            overrides={{
              Text: {
                weight: "semibold",
              },
            }}
            symbol={symbol}
            usdValue={getUsdPrice(currencyIdentifier, totalValue)}
            value={totalValue}
          />
        </OrdersSummaryItemSide>
      </OrdersSummaryItem>

      <Separator />

      {orders.length === 1 && (
        <OrdersSummaryItem>
          <OrdersSummaryItemContent>
            <OrdersSummaryItemDescription>
              {t("floorDifference")}
            </OrdersSummaryItemDescription>
          </OrdersSummaryItemContent>
          <OrdersSummaryItemSide>
            <OrdersSummaryItemDescription className="font-mono">
              {floorPriceDifference ? (
                <>
                  <NumberDisplay
                    display="percent"
                    value={floorPriceDifference}
                  />{" "}
                  {floorPriceDifference.gt(0)
                    ? t("aboveFloor")
                    : t("belowFloor")}
                </>
              ) : (
                "—"
              )}
            </OrdersSummaryItemDescription>
          </OrdersSummaryItemSide>
        </OrdersSummaryItem>
      )}

      {orderSide === "listing" && (
        <OrdersSummaryItem>
          <OrdersSummaryItemContent>
            <OrdersSummaryItemDescription>
              {t("platformFees")}{" "}
              {!Number.isNaN(openseaFeePercentage) && (
                <>
                  (
                  <NumberDisplay
                    display="percent"
                    value={openseaFeePercentage}
                  />
                  )
                </>
              )}
            </OrdersSummaryItemDescription>
          </OrdersSummaryItemContent>
          <OrdersSummaryItemSide>
            <OrdersSummaryTokenValue
              isFee
              symbol={symbol}
              usdValue={getUsdPrice(currencyIdentifier, openseaFee)}
              value={openseaFee}
            />
          </OrdersSummaryItemSide>
        </OrdersSummaryItem>
      )}

      {displayCreatorFee && (
        <OrdersSummaryItem>
          <OrdersSummaryItemContent>
            <Flex className="items-center gap-3">
              <Flex className="items-center gap-1.5">
                <OrdersSummaryItemDescription className="shrink-0">
                  {t("creatorFees")}{" "}
                  {!Number.isNaN(creatorFeePercentage) && (
                    <>
                      (
                      <NumberDisplay
                        display="percent"
                        value={creatorFeePercentage}
                      />
                      )
                    </>
                  )}
                </OrdersSummaryItemDescription>

                <CreatorEarningsTooltip
                  creatorFeeEnforced={creatorFeeEnforced}
                />
              </Flex>

              <Media greaterThanOrEqual="md">{creatorFeeToggle}</Media>
            </Flex>
            <Media lessThan="md">{creatorFeeToggle}</Media>
          </OrdersSummaryItemContent>
          <OrdersSummaryItemSide>
            <OrdersSummaryTokenValue
              isFee
              overrides={{
                Text: {
                  className: classNames({
                    "line-through": !applyCreatorFee && creatorFeeToggle,
                  }),
                },
              }}
              symbol={symbol}
              usdValue={getUsdPrice(currencyIdentifier, creatorFee)}
              value={creatorFee}
            />
          </OrdersSummaryItemSide>
        </OrdersSummaryItem>
      )}

      {orderSide === "listing" && <Separator />}

      {orderSide === "listing" && (
        <OrdersSummaryItem>
          <OrdersSummaryItemContent>
            <OrdersSummaryItemTitle>
              {t("totalEstProceeds")}
            </OrdersSummaryItemTitle>
          </OrdersSummaryItemContent>
          <OrdersSummaryItemSide>
            <OrdersSummaryTokenValue
              symbol={symbol}
              usdValue={getUsdPrice(currencyIdentifier, totalProceeds)}
              value={totalProceeds}
              variant="result"
            />
          </OrdersSummaryItemSide>
        </OrdersSummaryItem>
      )}
    </OrdersSummary>
  )
}

export function CreateOrdersSummarySkeleton({
  orderSide,
}: Pick<CreateOrdersSummaryProps, "orderSide">) {
  const t = useTranslations("wallet.CreateOrdersSummary")

  return (
    <OrdersSummary>
      <OrdersSummaryItem>
        <OrdersSummaryItemContent>
          <OrdersSummaryItemTitle>
            {orderSide === "offer"
              ? t("totalOfferValue")
              : t("totalListingPrice")}
          </OrdersSummaryItemTitle>
        </OrdersSummaryItemContent>
        <OrdersSummaryItemSide>
          <OrdersSummaryTokenValueSkeleton />
        </OrdersSummaryItemSide>
      </OrdersSummaryItem>

      {orderSide === "listing" && <Separator />}

      {orderSide === "listing" && (
        <OrdersSummaryItem>
          <OrdersSummaryItemContent>
            <OrdersSummaryItemDescription>
              <Skeleton className="h-3.5 w-[120px]" />
            </OrdersSummaryItemDescription>
          </OrdersSummaryItemContent>
          <OrdersSummaryItemSide>
            <OrdersSummaryTokenValueSkeleton />
          </OrdersSummaryItemSide>
        </OrdersSummaryItem>
      )}

      {orderSide === "listing" && (
        <OrdersSummaryItem>
          <OrdersSummaryItemContent>
            <OrdersSummaryItemDescription>
              <Skeleton className="h-3.5 w-[120px]" />
            </OrdersSummaryItemDescription>
          </OrdersSummaryItemContent>
          <OrdersSummaryItemSide>
            <OrdersSummaryTokenValueSkeleton />
          </OrdersSummaryItemSide>
        </OrdersSummaryItem>
      )}

      {orderSide === "listing" && <Separator />}

      {orderSide === "listing" && (
        <OrdersSummaryItem>
          <OrdersSummaryItemContent>
            <OrdersSummaryItemTitle>
              {t("totalEstProceeds")}
            </OrdersSummaryItemTitle>
          </OrdersSummaryItemContent>
          <OrdersSummaryItemSide>
            <OrdersSummaryTokenValueSkeleton variant="result" />
          </OrdersSummaryItemSide>
        </OrdersSummaryItem>
      )}
    </OrdersSummary>
  )
}
